export function parseShoePositionCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  const meta = {};
  const positions = [];

  lines.forEach((line, lineIndex) => {
    const parts = line.split(';').map((s) => s.trim());
    const key = parts[0];
    const val = parts[1] || '';

    if (!key && !val) {
      return;
    }

    if (lineIndex === 0 && key && !key.startsWith('Position_') && !key.startsWith('Angle_') && key !== 'Bockpr_Name') {
      const compactParts = parts.filter((part) => part !== '');
      meta.name = compactParts[0] || key;
      const parsedQuantity = Number.parseInt(compactParts[1] || '', 10);
      if (Number.isFinite(parsedQuantity)) {
        meta.quantity = parsedQuantity;
      }
      if (compactParts[2]) {
        meta.description = compactParts[2];
      }
      return;
    }

    if (lineIndex === 1) {
      const compactParts = parts.filter((part) => part !== '');
      const parsedLength = Number.parseFloat((compactParts[0] || '').replace(',', '.'));
      const hasExtendedFormat = compactParts.length >= 5;
      const bendingIndex = hasExtendedFormat ? 2 : 1;
      const thicknessIndex = hasExtendedFormat ? 3 : 2;
      const widthIndex = hasExtendedFormat ? 4 : 3;
      const parsedBendingPosition = Number.parseInt(compactParts[bendingIndex] || '', 10);
      const parsedThickness = Number.parseFloat((compactParts[thicknessIndex] || '').replace(',', '.'));
      const parsedWidth = Number.parseFloat((compactParts[widthIndex] || '').replace(',', '.'));

      if (Number.isFinite(parsedLength)) {
        meta.lamellaLength = parsedLength;
      }
      if (Number.isFinite(parsedBendingPosition)) {
        meta.bendingPosition = parsedBendingPosition;
      }
      if (Number.isFinite(parsedThickness)) {
        meta.lamellaThickness = parsedThickness;
      }
      if (Number.isFinite(parsedWidth)) {
        meta.lamellaWidth = parsedWidth;
      }
    }

    if (key === 'Bockpr_Name') {
      meta.name = val;
      meta.date = parts[2] || '';
    } else if (key === 'Zahl_Saeulen') {
      meta.columns = parseInt(val, 10);
    } else if (key.startsWith('Position_')) {
      const idx = parseInt(key.replace('Position_', ''), 10);
      if (!positions[idx - 1]) positions[idx - 1] = {};
      positions[idx - 1].position = val.replace(',', '.');
      positions[idx - 1].index = idx;
    } else if (key.startsWith('Angle_')) {
      const idx = parseInt(key.replace('Angle_', ''), 10);
      if (!positions[idx - 1]) positions[idx - 1] = {};
      positions[idx - 1].angle = val.replace(',', '.');
    }
  });

  return { meta, positions: positions.filter(Boolean) };
}

export function parseLslFile(text) {
  const lines = text.split(/\r?\n/);
  const groups = [];
  let referenceFromOriginMm = null;
  let referenceFromExtremityMm = null;
  let separatorCount = 0;

  for (const line of lines) {
    const trimmed = line.trim();
    if (referenceFromOriginMm == null && referenceFromExtremityMm == null && trimmed.includes('!') && trimmed.includes('.mm')) {
      const parts = line.split('!').map((part) => part.trim());
      if (parts.length >= 3) {
        const fromOrigin = Number.parseFloat((parts[1] || '').replace('mm', '').replace(',', '.'));
        const fromExtremity = Number.parseFloat((parts[2] || '').replace('mm', '').replace(',', '.'));
        referenceFromOriginMm = Number.isFinite(fromOrigin) ? fromOrigin : null;
        referenceFromExtremityMm = Number.isFinite(fromExtremity) ? fromExtremity : null;
      }
    }

    if (trimmed.startsWith('---')) {
      separatorCount += 1;
      if (separatorCount >= 4) {
        break;
      }
      continue;
    }

    if (separatorCount !== 3 || !trimmed) {
      continue;
    }

    const parts = line.split('!').map((part) => part.trim());
    if (parts.length < 3) {
      continue;
    }

    const aboutage = parts[0].split(/\s+/).filter(Boolean);
    const extremity = parts[2].split(/\s+/).filter(Boolean);

    const count = Number.parseInt(aboutage[0], 10);
    const length = Number.parseFloat((aboutage[1] || '0').replace(',', '.'));

    if (!Number.isFinite(count) || !Number.isFinite(length)) {
      continue;
    }

    groups.push({
      count,
      length,
      type: aboutage[2] || '',
      position: Number.parseFloat((extremity[0] || '0').replace(',', '.').replace(/^\-$/, '0')) || 0,
      offset: Number.parseFloat((extremity[1] || '0').replace(',', '.').replace(/^\-$/, '0')) || 0,
      code: extremity[2] || '',
      repeatedCount: Number.parseInt(extremity[3] || '0', 10) || 0
    });
  }

  const shoeSpacingMm = 400;
  const curveShoeFloat = referenceFromOriginMm != null ? referenceFromOriginMm / shoeSpacingMm : null;
  const curveShoeIndex = curveShoeFloat != null ? Math.floor(curveShoeFloat) : null;

  return {
    raw: text,
    groups,
    totalLamellas: groups.reduce((sum, group) => sum + group.count, 0),
    referenceFromOriginMm,
    referenceFromExtremityMm,
    curveShoeFloat,
    curveShoeIndex,
    shoeSpacingMm
  };
}

function toFiniteNumber(value) {
  const parsed = Number.parseFloat(String(value ?? '').replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}

export function normalizeShoePositions(shoePositions = [], fallbackPositions = []) {
  const preferred = Array.isArray(shoePositions) && shoePositions.length ? shoePositions : fallbackPositions;

  return preferred
    .map((entry, index) => {
      const position = toFiniteNumber(entry?.position);
      const angle = toFiniteNumber(entry?.angle);
      if (!Number.isFinite(position) || !Number.isFinite(angle)) {
        return null;
      }

      const parsedIndex = Number.parseInt(entry?.index, 10);
      return {
        index: Number.isFinite(parsedIndex) && parsedIndex > 0 ? parsedIndex : index + 1,
        position,
        angle
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.index - right.index);
}

export function normalizeLamellaSequence(sequence = []) {
  return (Array.isArray(sequence) ? sequence : [])
    .map((entry, index) => {
      const length = toFiniteNumber(entry?.length);
      if (!Number.isFinite(length) || length <= 0) {
        return null;
      }

      return {
        id: entry?.id ?? entry?.lamellaNo ?? index + 1,
        quality: entry?.quality || entry?.grade || '',
        length,
        startPosition: toFiniteNumber(entry?.startPosition),
        endPosition: toFiniteNumber(entry?.endPosition),
        leftGap: toFiniteNumber(entry?.leftGap),
        rightGap: toFiniteNumber(entry?.rightGap)
      };
    })
    .filter(Boolean);
}
